<template>
  <div class="view-responses">
    <!-- Header with Logo -->
    <div v-if="formData.logoUrl" class="form-logo text-center mb-3">
      <img :src="formData.logoUrl" alt="Company Logo" style="max-height: 60px; max-width: 180px; object-fit: contain;" />
    </div>

    <!-- KPIs -->
    <b-row class="mb-4">
      <b-col md="3" class="mb-3">
        <b-card class="stat-card text-center">
          <feather-icon icon="UsersIcon" size="24" class="text-primary mb-2" />
          <h4 class="mb-1">{{ allResponses.length }}</h4>
          <p class="text-muted mb-0">Total Responses</p>
        </b-card>
      </b-col>
      <b-col md="3" class="mb-3">
        <b-card class="stat-card text-center">
          <feather-icon icon="FileTextIcon" size="24" class="text-success mb-2" />
          <h4 class="mb-1">{{ totalQuestions }}</h4>
          <p class="text-muted mb-0">Total Questions</p>
        </b-card>
      </b-col>
      <b-col md="3" class="mb-3">
        <b-card class="stat-card text-center">
          <feather-icon icon="TrendingUpIcon" size="24" class="text-info mb-2" />
          <h4 class="mb-1">{{ averageCompletionRate }}%</h4>
          <p class="text-muted mb-0">Completion Rate</p>
        </b-card>
      </b-col>
      <b-col md="3" class="mb-3">
        <b-card class="stat-card text-center">
          <feather-icon icon="ClockIcon" size="24" class="text-warning mb-2" />
          <h4 class="mb-1">{{ recentResponses }}</h4>
          <p class="text-muted mb-0">Recent (7 days)</p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Search Filter -->
    <b-card class="mb-3">
      <b-form-input v-model="search" placeholder="Search by question, answer, or user..." class="mb-0" />
    </b-card>

    <!-- Download Button -->
    <b-card class="mb-3">
      <b-button variant="success" @click="exportCSV">
        <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
        Download All Responses (CSV)
      </b-button>
    </b-card>

    <!-- Graph: Answer Distribution -->
    <b-card class="mb-4">
      <b-row>
        <b-col md="6">
          <b-form-group label="Select Question for Graph">
            <b-form-select v-model="selectedQuestionId" :options="questionOptions" />
          </b-form-group>
        </b-col>
      </b-row>
      <canvas v-if="selectedQuestionId" ref="chart" height="120"></canvas>
    </b-card>

    <!-- Responses Table -->
    <b-card>
      <div class="table-responsive">
        <b-table
          :items="filteredRows"
          :fields="tableFields"
          small
          responsive
          bordered
          striped
          hover
          class="responses-table"
        >
          <template #cell(submittedAt)="data">
            <div class="text-nowrap">{{ formatDate(data.item.submittedAt) }}</div>
          </template>
          <template #cell(userId)="data">
            <span :class="data.item.userId === 'Anonymous' ? 'text-muted font-italic' : 'font-weight-bold'">
              {{ data.item.userId }}
            </span>
          </template>
          <template #cell(question)="data">
            <div class="question-text">{{ data.item.question }}</div>
          </template>
          <template #cell(response)="data">
            <div class="response-text">
              {{ data.item.response }}
              <template v-if="data.item.conditionalText">
                <br/>
                <span class="text-muted small">Conditional: {{ data.item.conditionalText }}</span>
              </template>
            </div>
          </template>
        </b-table>
      </div>
      <div v-if="filteredRows.length === 0" class="text-center py-5">
        <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
        <h5 class="text-muted">No responses found</h5>
        <p class="text-muted">This form has no responses yet.</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BCard, BButton, BFormGroup, BFormInput, BFormSelect, BTable, BRow, BCol } from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import FormsService from '@/services/forms-service'
// import Chart from 'chart.js/auto'

export default {
  name: 'ViewResponses',
  components: { BCard, BButton, BFormGroup, BFormInput, BFormSelect, BTable, BRow, BCol, FeatherIcon },
  data() {
    return {
      formData: {},
      allResponses: [],
      tableRows: [],
      tableFields: [
        { key: 'submittedAt', label: 'Submission Date', sortable: true },
        { key: 'userId', label: 'User ID', sortable: true },
        { key: 'question', label: 'Question', sortable: true },
        { key: 'response', label: 'Response' }
      ],
      search: '',
      selectedQuestionId: '',
      chartInstance: null,
    }
  },
  computed: {
    filteredRows() {
      if (!this.search) return this.tableRows
      const s = this.search.toLowerCase()
      return this.tableRows.filter(row =>
        row.question.toLowerCase().includes(s) ||
        row.response.toLowerCase().includes(s) ||
        row.userId.toLowerCase().includes(s)
      )
    },
    questionOptions() {
      return (this.formData.questions || [])
        .filter(q => q.type !== 'header')
        .map(q => ({ value: q.id, text: q.text }))
    },
    totalQuestions() {
      return (this.formData.questions || []).filter(q => q.type !== 'header').length
    },
    averageCompletionRate() {
      if (this.allResponses.length === 0) return 0
      const totalPossibleAnswers = this.totalQuestions * this.allResponses.length
      let totalAnswers = 0
      this.allResponses.forEach(response => {
        Object.keys(response.answers).forEach(key => {
          const answer = response.answers[key]
          if (answer && (typeof answer === 'string' || Array.isArray(answer) || (typeof answer === 'object' && answer.answer))) {
            totalAnswers++
          }
        })
      })
      return Math.round((totalAnswers / totalPossibleAnswers) * 100)
    },
    recentResponses() {
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
      return this.allResponses.filter(r => new Date(r.submittedAt) > sevenDaysAgo).length
    },
  },
  watch: {
    selectedQuestionId() {
      this.renderChart()
    },
    tableRows() {
      this.renderChart()
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async loadData() {
      try {
        // Load form data
        const formResponse = await FormsService.getFormById(this.$route.params.id)
        if (formResponse.success && formResponse.data) {
          this.formData = formResponse.data
        } else {
          throw new Error('Form not found')
        }
        
        // Load all responses for this form
        const responsesResponse = await FormsService.getFormResponses(this.$route.params.id)
        if (responsesResponse.success && responsesResponse.data) {
          this.allResponses = responsesResponse.data
        } else {
          this.allResponses = []
        }
        
        // Build table rows: one row per question per response
        const rows = []
        allResponses.forEach(response => {
          (this.formData.questions || []).forEach(question => {
            if (question.type !== 'header') {
              let answer = response.answers[question.id]
              let responseText = ''
              if (typeof answer === 'object' && answer && answer.answer) {
                responseText = answer.answer
                if (answer.reason) responseText += ` (Reason: ${answer.reason})`
              } else if (Array.isArray(answer)) {
                responseText = answer.join(', ')
              } else {
                responseText = answer || 'No answer'
              }
              // Add conditional text if present
              let conditionalText = ''
              if (question.type === 'radio' && response.answers[question.id + '_conditional']) {
                conditionalText = response.answers[question.id + '_conditional']
              }
              rows.push({
                submittedAt: response.submittedAt,
                userId: response.userId || 'Anonymous',
                question: question.text,
                response: responseText,
                conditionalText,
                questionId: question.id
              })
            }
          })
        })
        this.tableRows = rows
        // Set default selected question for graph
        if (!this.selectedQuestionId && this.questionOptions.length > 0) {
          this.selectedQuestionId = this.questionOptions[0].value
        }
        this.$nextTick(this.renderChart)
      } catch (error) {
        this.$bvToast.toast('Error loading form responses', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    renderChart() {
      if (!this.selectedQuestionId || !this.$refs.chart) return
      // Gather answer frequencies for the selected question
      const freq = {}
      this.allResponses.forEach(r => {
        const ans = r.answers[this.selectedQuestionId]
        if (Array.isArray(ans)) {
          ans.forEach(a => { freq[a] = (freq[a] || 0) + 1 })
        } else if (typeof ans === 'object' && ans && ans.answer) {
          freq[ans.answer] = (freq[ans.answer] || 0) + 1
        } else if (ans) {
          freq[ans] = (freq[ans] || 0) + 1
        }
      })
      const labels = Object.keys(freq)
      const data = Object.values(freq)
      // Destroy previous chart if exists
      if (this.chartInstance) {
        this.chartInstance.destroy()
      }
      this.chartInstance = new Chart(this.$refs.chart, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Response Count',
            data,
            backgroundColor: '#7367f0',
            maxBarThickness: 60,
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            title: { display: true, text: 'Answer Distribution' }
          },
          scales: {
            x: { title: { display: true, text: 'Answer' } },
            y: { title: { display: true, text: 'Count' }, beginAtZero: true, precision: 0 }
          }
        }
      })
    },
    async exportCSV() {
      if (!this.tableRows.length) return
      try {
        const blob = await FormsService.exportResponses(this.$route.params.id, 'csv')
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${this.formData.title || 'responses'}_${new Date().toISOString().split('T')[0]}.csv`
        a.click()
        URL.revokeObjectURL(url)
      } catch (error) {
        this.$bvToast.toast('Error exporting responses', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="scss" scoped>
.view-responses {
  .form-logo {
    margin-bottom: 10px;
  }
  
  .header-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
    
    h4, p {
      color: white;
    }
  }
  
  .responses-table {
    font-size: 0.95rem;
    
    th {
      background: #f8f9fa;
      font-weight: 600;
      color: #2c3e50;
      border-color: #dee2e6;
      vertical-align: middle;
    }
    
    td {
      vertical-align: top;
      border-color: #dee2e6;
    }
    
    .question-text {
      font-weight: 600;
      color: #2c3e50;
      font-size: 0.9rem;
      line-height: 1.4;
      max-width: 300px;
    }
    
    .response-text {
      color: #495057;
      font-size: 0.9rem;
      line-height: 1.4;
      max-width: 400px;
      word-wrap: break-word;
    }
  }
  
  .table-responsive {
    border-radius: 8px;
    overflow: hidden;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .view-responses {
    .header-card {
      .d-flex {
        flex-direction: column;
        gap: 16px;
      }
    }
    
    .responses-table {
      font-size: 0.8rem;
      
      .question-text {
        max-width: 200px;
      }
      
      .response-text {
        max-width: 250px;
      }
    }
  }
}
</style>

<style scoped>
:root {
  --form-bg: #fff;
  --form-text: #222;
  --form-border: #ebe9f1;
}
@media (prefers-color-scheme: dark) {
  :root {
    --form-bg: #181824;
    --form-text: #f1f1f1;
    --form-border: #33344a;
  }
}
.view-responses, .b-card, .b-form-group {
  background: var(--form-bg) !important;
  color: var(--form-text) !important;
  border-color: var(--form-border) !important;
}
.b-form-input, .b-form-select, .b-form-textarea {
  font-size: 0.98rem;
  padding: 6px 10px;
}
.b-form-group, .stat-card {
  margin-bottom: 8px !important;
}
.b-card {
  padding: 12px 14px !important;
}
</style> 